import React, { useMemo, useRef } from "react";
import TimeSelect from "./TimeSelect";
import { createGroupedBlurHandler } from "../shared/hooks/useBlurAutoSave";
import { fieldCommitDateInputKeyUp } from "../shared/utils/fieldCommitOnEnterKeyDown";

const FORM_ROW_TIME_STYLE = { flex: "1 1 88px", minWidth: "88px", width: "auto" };
const INLINE_TIME_STYLE = { flex: "0 0 96px", minWidth: "96px" };

/**
 * Shared date + time editor(s) with grouped blur so focus can move between controls
 * without exiting inline edit / autosave.
 *
 * Layouts:
 * - `inline` — compact date + optional single time (Task / Time Blocking details)
 * - `form-row` — `.form-datetime-row` date + start/end times (+ optional `children` for Repeat)
 *
 * Pass `onBlur` for blur-to-save / exit-edit; omit for always-open forms.
 */
export default function DateTimeFieldGroup({
  layout = "inline",
  date,
  onDateChange,
  time,
  onTimeChange,
  startTime,
  onStartTimeChange,
  endTime,
  onEndTimeChange,
  timeOptions,
  onBlur,
  onKeyDown,
  autoFocus = false,
  disabled = false,
  required = false,
  dropdownPortal = true,
  maxVisibleItems = 5,
  dateId,
  timeId,
  dateAriaLabel = "Date",
  timeAriaLabel = "Time",
  timeTitle,
  startTimeLabel = "Start time",
  endTimeLabel = "End time",
  dateClassName,
  dateStyle,
  timeSelectStyle,
  className,
  style,
  dateInputRef: dateInputRefProp,
  showDatePickerOnClick = false,
  onTimeOpen,
  onDateKeyUp,
  children,
}) {
  const rootRef = useRef(null);
  const internalDateRef = useRef(null);
  const dateInputRef = dateInputRefProp || internalDateRef;
  const groupedBlur = useMemo(
    () => (typeof onBlur === "function" ? createGroupedBlurHandler(rootRef, onBlur) : undefined),
    [onBlur]
  );

  const isFormRow = layout === "form-row";
  const isRange = typeof onStartTimeChange === "function" || typeof onEndTimeChange === "function";
  const resolvedDateClass =
    dateClassName != null
      ? dateClassName
      : isFormRow
        ? "panel-datetime-control"
        : "input";

  const handleDateKeyDown = (e) => {
    if (typeof onKeyDown === "function") onKeyDown(e);
  };

  const handleDateKeyUp = (e) => {
    if (typeof onDateKeyUp === "function") {
      onDateKeyUp(e);
      return;
    }
    fieldCommitDateInputKeyUp(e);
  };

  const dateInput = (
    <input
      ref={dateInputRef}
      id={dateId}
      type="date"
      value={date || ""}
      required={required}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-label={dateAriaLabel}
      onChange={(e) => onDateChange?.(e.target.value)}
      onKeyDown={handleDateKeyDown}
      onKeyUp={handleDateKeyUp}
      className={resolvedDateClass}
      style={dateStyle != null ? dateStyle : isFormRow ? { width: "100%" } : { flex: "1 1 auto", minWidth: 0 }}
    />
  );

  const dateBlock = showDatePickerOnClick ? (
    <div
      className={isFormRow ? "form-datetime-row__date" : undefined}
      style={{ cursor: "pointer", ...(isFormRow ? undefined : { display: "contents" }) }}
      onClick={() => dateInputRef.current?.showPicker?.()}
      role={isFormRow ? "button" : undefined}
      tabIndex={isFormRow ? 0 : undefined}
      onKeyDown={
        isFormRow
          ? (e) => {
              if (e.key === "Enter") dateInputRef.current?.showPicker?.();
            }
          : undefined
      }
    >
      {dateInput}
    </div>
  ) : isFormRow ? (
    <div className="form-datetime-row__date">{dateInput}</div>
  ) : (
    dateInput
  );

  const singleTime =
    !isRange && typeof onTimeChange === "function" ? (
      <TimeSelect
        id={timeId}
        value={time || ""}
        options={timeOptions}
        onChange={onTimeChange}
        onBlur={groupedBlur}
        onKeyDown={onKeyDown}
        onOpen={onTimeOpen}
        disabled={disabled}
        maxVisibleItems={maxVisibleItems}
        dropdownPortal={dropdownPortal}
        title={timeTitle || timeAriaLabel}
        style={timeSelectStyle || (isFormRow ? FORM_ROW_TIME_STYLE : INLINE_TIME_STYLE)}
      />
    ) : null;

  const startEndTimes = isRange ? (
    <>
      <div className="form-datetime-row__time">
        <span className="panel-inline-label">{startTimeLabel}</span>
        <TimeSelect
          value={startTime || ""}
          options={timeOptions}
          onChange={onStartTimeChange}
          onBlur={groupedBlur}
          onKeyDown={onKeyDown}
          disabled={disabled}
          maxVisibleItems={maxVisibleItems}
          dropdownPortal={dropdownPortal}
          style={timeSelectStyle || FORM_ROW_TIME_STYLE}
        />
      </div>
      <div className="form-datetime-row__time">
        <span className="panel-inline-label">{endTimeLabel}</span>
        <TimeSelect
          value={endTime || ""}
          options={timeOptions}
          onChange={onEndTimeChange}
          onBlur={groupedBlur}
          onKeyDown={onKeyDown}
          disabled={disabled}
          maxVisibleItems={maxVisibleItems}
          dropdownPortal={dropdownPortal}
          style={timeSelectStyle || FORM_ROW_TIME_STYLE}
        />
      </div>
    </>
  ) : null;

  const rootClass = [isFormRow ? "form-datetime-row" : null, className].filter(Boolean).join(" ");
  const rootStyle =
    style != null
      ? style
      : isFormRow
        ? undefined
        : { display: "flex", gap: "6px", alignItems: "center", flexWrap: "nowrap" };

  return (
    <div ref={rootRef} className={rootClass || undefined} style={rootStyle} onBlur={groupedBlur}>
      {dateBlock}
      {isFormRow && singleTime ? (
        <div className="form-datetime-row__time">
          <span className="panel-inline-label">Time</span>
          {singleTime}
        </div>
      ) : (
        singleTime
      )}
      {startEndTimes}
      {children}
    </div>
  );
}
